import axios from "axios";
import store from '@/store'
import {accessToken} from './httpAccessVue'
import {getBase64Token, getToken, localRead} from "./util";

// 创建axios实例池以复用
const instancePool = {};
// 请求缓存
const requestCache = new Map();
// 用于防抖的定时器Map
const debounceTimers = new Map();

// 防抖函数
function debounce(fn, delay, key) {
  return function() {
    const context = this;
    const args = arguments;
    if (debounceTimers.has(key)) {
      clearTimeout(debounceTimers.get(key));
    }
    const timer = setTimeout(() => {
      fn.apply(context, args);
      debounceTimers.delete(key);
    }, delay);
    debounceTimers.set(key, timer);
  };
}

const addErrorLog = errorInfo => {
  const { statusText,status,request:{responseURL}} = errorInfo
  let info = {
    type: 'ajax',
    code: status,
    mes: statusText,
    url: responseURL
  }
  if(!responseURL.includes('save_error_logger')) store.dispatch('addErrorLog',info)
};

class HttpRequest {
  constructor (baseUrl = baseURL){
    this.baseUrl = baseUrl
    this.queue = {}
    // 优化超时设置，区分不同类型请求
    this.timeouts = {
      default: 60000, // 默认1分钟
      short: 10000,   // 短连接10秒
      long: 600000    // 长连接10分钟
    }
  }

  getInsideConfig(){
    const config = {
      baseURL: this.baseUrl,
      headers: {},
      timeout: this.timeouts.default, // 默认超时设置
      // 添加缓存配置选项
      useCache: false,
      cacheKey: '',
      cacheTime: 5000, // 默认缓存5秒
      // 防抖配置选项
      debounce: false,
      debounceTime: 300
    }
    return config
  }

  destory (url) {
    delete this.queue[url]
    if(!Object.keys(this.queue).length){
      // 队列为空时可以执行一些清理操作
    }
  }

  // 生成缓存键
  generateCacheKey(config) {
    const { url, method, params, data } = config;
    return `${url}_${method}_${JSON.stringify(params || {})}_${JSON.stringify(data || {})}`;
  }

  // 获取或创建axios实例（复用实例）
  getAxiosInstance(baseUrl) {
    if (!instancePool[baseUrl]) {
      instancePool[baseUrl] = axios.create({ baseURL: baseUrl });
      // 只对实例进行一次拦截器设置
      this.setupInstanceInterceptors(instancePool[baseUrl]);
    }
    return instancePool[baseUrl];
  }

  // 设置实例拦截器
  setupInstanceInterceptors(instance) {
    // 这里设置通用拦截器，而不是每次请求都设置
    instance.interceptors.request.use(
      config => {
        // 不携带cookie
        config.withCredentials = false;

        let token = getToken();
        let base64Token = getBase64Token();
        let lang = localRead('local');
        let method = config.method;

        if(lang){
          try {
            if(method==='post'){
              config.data = config.data || {};
              config.data.language = lang;
            }
            if (method === 'get') {
              config.params = config.params || {};
              config.params.language = lang;
            }
          } catch (e) {
            config.params = {language: lang};
          }
        }

        config.headers['Access-Token'] = accessToken();

        if(token){
          if (config.headers['Authorization'] === undefined) {
            config.headers['Authorization'] = token; //头部传入token
          }
        }

        // 针对所有的control请求加上Auth
        if (config.url && config.url.indexOf('control') > -1 && base64Token &&
            config.url.indexOf('login.json') < 0 && config.url.indexOf('getServerMac.json') < 0) {
          if (!base64Token || base64Token == 'false' || base64Token=='undefined') {
            base64Token = token;
          }
          config.headers['Authorization'] = base64Token;
        }

        // 根据请求类型设置超时
        if (config.timeoutType) {
          config.timeout = this.timeouts[config.timeoutType] || this.timeouts.default;
        }

        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    instance.interceptors.response.use(
      res => {
        const { data, status, headers } = res;
        return {data, status, headers};
      },
      error => {
        let language = localRead('local');
        let mes = '';
        if(language==='zh-CN'){
          mes = 'token已过期';
        }else if(language==='zh-TW'){
          mes = 'token已過期';
        }else if(language==='en-US'){
          mes = 'Token expired';
        }

        if(error.response && (error.response.data.code === 2025 || error.response.data.code === 2026)){
          // 错误处理代码保留但注释掉
        }
        return Promise.reject(error);
      }
    );
  }

  request(options) {
    const config = Object.assign(this.getInsideConfig(), options);
    const instance = this.getAxiosInstance(config.baseURL);

    // 生成缓存键
    const cacheKey = config.cacheKey || this.generateCacheKey(config);

    // 实现请求缓存
    if (config.useCache && requestCache.has(cacheKey)) {
      const cachedItem = requestCache.get(cacheKey);
      const now = Date.now();
      // 检查缓存是否过期
      if (now - cachedItem.timestamp < config.cacheTime) {
        return Promise.resolve(cachedItem.data);
      } else {
        // 缓存过期，移除
        requestCache.delete(cacheKey);
      }
    }

    // 实现请求防抖
    if (config.debounce) {
      const debounceKey = `${cacheKey}_debounce`;
      return new Promise((resolve, reject) => {
        const debouncedRequest = debounce(() => {
          instance(config)
            .then(response => {
              // 存储到缓存
              if (config.useCache) {
                requestCache.set(cacheKey, {
                  data: response,
                  timestamp: Date.now()
                });
              }
              resolve(response);
            })
            .catch(error => {
              reject(error);
            });
        }, config.debounceTime, debounceKey);

        debouncedRequest();
      });
    }

    // 普通请求
    return instance(config).then(response => {
      // 存储到缓存
      if (config.useCache) {
        requestCache.set(cacheKey, {
          data: response,
          timestamp: Date.now()
        });
      }
      return response;
    });
  }

  // 清除特定缓存
  clearCache(pattern) {
    if (pattern) {
      // 清除匹配模式的缓存
      for (let key of requestCache.keys()) {
        if (key.includes(pattern)) {
          requestCache.delete(key);
        }
      }
    } else {
      // 清除所有缓存
      requestCache.clear();
    }
  }
}
export default HttpRequest



// WEBPACK FOOTER //
// ./src/libs/axios.js